import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { errText, fetchBlob, isDocx, isImage, isPdf } from "./resourceApi";

/** Inline preview of a library file (PDF / image; DOCX via its rendered PDF preview). */
export function ResourcePreview({ path, file, className = "h-[560px]", testId = "resource-preview" }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const ct = file?.content_type;
  const canShow = isPdf(ct) || isImage(ct) || (isDocx(ct) && file?.preview_path);

  useEffect(() => {
    if (!path || !canShow) return undefined;
    let objectUrl = null;
    let live = true;
    setUrl(null);
    setError(null);
    fetchBlob(path, { preview: true })
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((e) => live && setError(errText(e)));
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [path, canShow]);

  const box = `rounded-lg border border-zinc-200 bg-zinc-50 ${className}`;
  if (!canShow) {
    return (
      <div className={`${box} flex flex-col items-center justify-center gap-2 text-sm text-zinc-500`} data-testid={testId}>
        <FileText className="h-8 w-8 text-zinc-400" /> No preview available — download to open.
      </div>
    );
  }
  if (error) return <div className={`${box} p-4 text-sm text-red-700`} data-testid={testId}>{error}</div>;
  if (!url) {
    return (
      <div className={`${box} flex items-center justify-center text-sm text-zinc-500`} data-testid={testId}>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading preview…
      </div>
    );
  }
  return isImage(ct)
    ? <div className={`${box} flex items-center justify-center overflow-auto`} data-testid={testId}>
        <img src={url} alt={file?.filename} className="max-h-full max-w-full object-contain" />
      </div>
    : <iframe title="Resource preview" src={url} className={`${box} w-full`} data-testid={testId} />;
}
